"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-foreground text-background rounded-none flex items-center justify-center mb-4 border border-foreground/20">
          <Mail className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground font-sans uppercase tracking-tight">Message Sent!</h3>
        <p className="text-foreground/80 max-w-xs mx-auto font-medium">{successMessage}</p>
        <Button variant="outline" className="mt-6 rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background" onClick={() => window.location.reload()}>
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold uppercase text-xs tracking-widest text-foreground/70">Your Name</FormLabel>
              <FormControl>
                <Input {...field} className="bg-background/50 rounded-none border-foreground/20 focus-visible:border-foreground h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold uppercase text-xs tracking-widest text-foreground/70">Email Address</FormLabel>
              <FormControl>
                <Input type="email" {...field} className="bg-background/50 rounded-none border-foreground/20 focus-visible:border-foreground h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold uppercase text-xs tracking-widest text-foreground/70">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" {...field} className="bg-background/50 rounded-none border-foreground/20 focus-visible:border-foreground h-12" />
              </FormControl>
              <FormDescription className="text-[10px] text-foreground/60">Optional – for quicker response</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold uppercase text-xs tracking-widest text-foreground/70">Subject</FormLabel>
              <FormControl>
                <Input placeholder="Reason for contact" {...field} className="bg-background/50 rounded-none border-foreground/20 focus-visible:border-foreground h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-bold uppercase text-xs tracking-widest text-foreground/70">Message</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[150px] w-full rounded-none border border-foreground/20 bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y focus-visible:border-foreground")} placeholder="Tell us how we can help you..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-none font-bold">
          {submitError}
        </div>}

      <Button type="submit" variant="default" className="w-full md:w-auto min-w-[220px] rounded-none font-bold uppercase tracking-widest py-7 text-base bg-primary text-background hover:bg-primary/90" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin text-background" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  return <div className="bg-gradient-to-t from-[#F9CBD6] via-[#F2AFBC] via-[#9E182B] to-[#F2E0D2] relative shadow-[inset_0_0_100px_rgba(158,24,43,0.1)]">
    <section id="get-in-touch" className="text-foreground py-24 px-4 md:px-8 lg:px-12 overflow-hidden relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-start">
          
          <div className="flex flex-col justify-center h-full space-y-6">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-foreground font-sans uppercase leading-none">
                How You Can <br />
                <span className="text-foreground/80">Reach Us</span>
              </h2>
              
              
            </div>

            <div className="space-y-6">
              <div className="group">
                <p className="text-xs font-bold text-foreground/60 uppercase tracking-widest mb-3">Call or Text</p>
                <Link to="tel:5126359324" className="flex items-center gap-4 text-3xl md:text-4xl font-extrabold text-foreground hover:opacity-70 transition-opacity">
                  <Phone className="size-8 md:size-10 shrink-0 stroke-[2.5px]" />
                  <span>(512) 635-9324</span>
                </Link>
              </div>

              <div className="group">
                <p className="text-xs font-bold text-foreground/60 uppercase tracking-widest mb-3">Email Us</p>
                <Link to="mailto:PureDetailCleaningATX@Gmail.com" className="flex items-center gap-4 text-xl md:text-2xl font-extrabold text-foreground hover:opacity-70 transition-opacity whitespace-nowrap">
                  <Mail className="size-8 md:size-10 shrink-0 stroke-[2.5px]" />
                  <span>DallasD@PureDetailCleaning.com</span>
                </Link>
              </div>
            </div>

            
          </div>

          
          <div className="bg-background/80 backdrop-blur-sm p-8 md:p-12 rounded-none shadow-2xl border border-foreground/5 flex-1 h-full">
            <div className="mb-10">
              <h3 className="text-3xl font-extrabold text-foreground font-sans uppercase tracking-tight">Send Us a Message</h3>
              <div className="h-1 w-20 bg-primary mt-4 mb-6"></div>
              <p className="text-foreground/80 font-medium">Let us know how we can help. We take pride in our response time and will reach out shortly to discuss your cleaning needs.</p>
            </div>

            <FormProvider formId="contact_form_contact" sectionName="get-in-touch" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you! We've received your message and will contact you soon.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>
  </div>;
}